/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { withRouter } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { APPLICATION_STRINGS } from '../strings'

import usePrevious from '../hooks/usePrevious'

import SidebarModal from './SidebarModal'
import Button from './Button'
import ConfirmSplitPaymentDialog from './dialog/ConfirmSplitPaymentDialog'
import { setBlockRichCheckoutOrderDialog, setKitchenPrintErrorDialogOpen, SetOfflineDialog } from '../VNDialogs/ActionCreators'

import styles from './CartModal.module.scss'

import { setBridgePaymentMethod } from '../actions/peripheral'

import CartItems from '../components/CartItems'

import ConfirmModalV2 from '../components/ConfirmModalV2'

import useOfflineModal from '../hooks/useOfflineModal'
import useRemoteOrderTotal from '../hooks/useRemoteOrderTotal'
import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'

import { getOrderInProgress } from '../selectors/order'
import { getRequirePaymentForTabs } from '../selectors/menus'
import { getKitchenPrintingEnabled } from '../selectors/menus'

import { bridgePaymentMethods } from '../reducers/peripheral'

import { ToastManager } from '../utils/toastManager'
import { isTabbed } from '../utils/orderStates'
import { getNetworkAvailableValue } from '../utils/networkConnected'
import { getTipOnCardReader } from '../selectors/peripheral'
import PayNowButton from '../VNTabs/components/PayNowButton'
import { getIsTabNameRequired } from '../VNTabs/Selectors'
import { setIsTabNameRequired } from '../VNTabs/ActionCreators'
import { setRoute } from '../VNRoutes/ActionCreators'
import { getPpiConfigurationError, getPpiConfigurations } from '../VNPPI/Selectors'
import { VNPPI_TENDERS } from '../VNPPI/Enums'
import { getTender } from '../VNPPI/Utils'
import { hasOpenPriceItem, cartHasPriceOverrideItem, getSubtotal } from '../selectors/cart'
import useSpinner from '../context/hooks/useSpinner'
import { setShouldIncrementOrderNumber } from '../VNOrders/ActionCreators'

import { nextPosOrderNumber } from '../utils/orderNumbers'

import { getBackbarMode } from '../selectors/config'
import { getIsOrderTaxExempt } from '../VNTaxes/Selectors'
import { isFeatureOn } from '../VNConfigurations/Selectors'
import { VNCONFIGS_FEATURE_ENABLEMENT_KEYS } from '../VNConfigurations/Enums'
import { useCharity } from '../VNOrders/hooks/useCharity'

const CartModal = ({
  history,
  hasCartItems,
  clearCart,
  className,
  cartItems,
  total,
  itemCount,
  discountsAvailable,
  noOpPayments,
  signatureRequired,
  tipSuggestions,
  cashEnabled,
  networkAvailable,
  ticketEnabled,
  mobilePaySetting,
  clearPromotionsOnOrder,
  giftCardEnabled,
  tabsModeEnabled,
  mutationInProgress,
  startATab,
  addItemsToTab,
  addItemsToTabOffline,
}) => {
  const classNames = cn(styles.cartModal, className)
  const [showCart, setShowCart] = useState(false)
  const [showSplitPayDialog, setShowSplitPayDialog] = useState(false)
  const tabNameRequired = useSelector(getIsTabNameRequired)
  const [showStartTabModal, setShowStartTabModal] = useState(tabNameRequired)
  const [showSelectTabPaymentMethod, setShowSelectTabPaymentMethod] = useState(false)
  const [checkIfOfflineWithCallback, offlineModal] = useOfflineModal()
  const [loadRemoteOrderTotal, { data: success, failed, loading }] = useRemoteOrderTotal()
  const cloudPrintingEnabled = useSelector((state) => getKitchenPrintingEnabled(state))
  const orderInProgress = useSelector((state) => getOrderInProgress(state))
  const prevOrderInProgress = usePrevious(orderInProgress)
  const requirePaymentForTabs = useSelector((state) => getRequirePaymentForTabs(state))
  const cartHasOpenItem = useSelector(hasOpenPriceItem)
  const cartHasPriceOverride = useSelector(cartHasPriceOverrideItem)
  const { startSpinner, showSpinner, stopSpinner } = useSpinner()

  const cardReaderData = history?.location?.state?.cardReaderData ?? null
  const paymentType = history?.location?.state?.paymentType ?? undefined
  const paymentId = history?.location?.state?.paymentId ?? undefined
  const redirectPath = history?.location?.pathname ?? undefined
  const dispatch = useDispatch()
  const tabNameInputRef = useRef()
  const processingPromotions = useRef(false)

  const buttonsDisabled = !hasCartItems
  const tipEnabled = !isEmpty(tipSuggestions)
  const tipOnCardReader = useSelector(getTipOnCardReader)
  const cardTipEnabledOnPos = !tipOnCardReader && tipEnabled
  const isBackBar = useSelector(getBackbarMode)
  const isMobileViewPort = useGetIsMobileScreen()
  const isTabOrder = isTabbed(prevOrderInProgress)
  const totalInCents = useSelector(getSubtotal)
  const { getRoundUpForCharityFeature } = useCharity()
  const isOrderTaxExempt = useSelector(getIsOrderTaxExempt)

  const disablePPISplitOffline = useSelector((state) =>
    isFeatureOn(state, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.DISABLE_PPI_SPLIT_OFFLINE),
  )

  let modal = null

  const isUniqueInvoiceNumbersEnabled = useSelector((state) =>
    isFeatureOn(state, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.UNIQUE_INVOICE_NUMBERS),
  )

  const confirmationNumber = nextPosOrderNumber()

  const ppiConfigurations = useSelector((state) => getPpiConfigurations(state))

  const ppiTicketTender = getTender(ppiConfigurations, VNPPI_TENDERS.TICKET_TENDER)
  const ppiQRPayTender = getTender(ppiConfigurations, VNPPI_TENDERS.QRPAY_TENDER)
  const ppiGiftcardTender = getTender(ppiConfigurations, VNPPI_TENDERS.GIFTCARD_TENDER)
  const ppiConfigurationError = useSelector(getPpiConfigurationError)

  const clearItemsAndPromotions = () => {
    dispatch(setShouldIncrementOrderNumber())
    clearCart()
    clearPromotionsOnOrder()
  }

  useEffect(() => {
    if (!hasCartItems) {
      setShowCart(false)
    }
  }, [hasCartItems])

  useEffect(() => {
    if (!tabNameRequired) {
      return
    }
    setShowStartTabModal(true)
    tabNameInputRef?.current?.focus()
  }, [tabNameRequired])

  useEffect(() => {
    if ((success || failed) && !processingPromotions.current) {
      handleCardTransaction()
    }
    processingPromotions.current = false
  }, [success, failed])

  useEffect(() => {
    if (showStartTabModal && showSpinner) {
      stopSpinner()
    }
  }, [showStartTabModal, showSpinner, stopSpinner])

  useEffect(() => {
    if (tabNameInputRef.current) {
      tabNameInputRef?.current?.select()
    }
  }, [])

  const processPromotions = async () => {
    try {
      processingPromotions.current = true
      await loadRemoteOrderTotal()
    } catch (e) {
      processingPromotions.current = false
      ToastManager.error(APPLICATION_STRINGS.PROMOTIONS_NOT_APPLIED)
      //continue processing if orderTotal fails and we can't apply promos
      return Promise.resolve()
    }
  }

  const getInitialInputValue = () => {
    return cardReaderData?.cardholderName?.trim() || ''
  }

  const initiateTabAndCloseModal = (tabName) => {
    startSpinner()
    setShowStartTabModal(false)
    dispatch(setIsTabNameRequired(false))
    initiateTab(tabName)
  }

  const initiateTab = async (tabName) => {
    if (!tabName) return

    if (getNetworkAvailableValue()) {
      await processPromotions()
    }

    //We need redirect to the current pathname once the tab is created in order to to clear
    //state in history which contains cardReaderData so it's not pulled in the next order
    const tabData = {
      tabName,
      redirectPath,
    }

    if (cardReaderData) {
      tabData.cardReaderData = cardReaderData
      tabData.paymentType = paymentType
      tabData.paymentId = paymentId
    }

    startATab(tabData)

    if (cloudPrintingEnabled && !getNetworkAvailableValue()) {
      dispatch(setKitchenPrintErrorDialogOpen(true))
    }
  }

  if (showSelectTabPaymentMethod) {
    modal = (
      <ConfirmModalV2
        headerText={'Tab Payment Method'}
        emptyButtonContainer
        displayCloseButton
        onCloseButtonClick={() => setShowSelectTabPaymentMethod(false)}
        classNames={{
          confirmModalV2: styles.ConfirmModalV2,
          dialogContainer: styles.dialogContainer,
          container: styles.confirmModalContainer,
          textBox: styles.textBox,
        }}
        subtext={
          <div className={styles.tabButtonContainer}>
            <Button
              useMui
              onClick={() => {
                dispatch(setBridgePaymentMethod(bridgePaymentMethods.executeAuthRequest))
                history.push('/card-reader', { redirectPath: '/concession-order', authTabs: true })
              }}
            >
              <span>{APPLICATION_STRINGS.AUTHORIZE_NOW}</span>
            </Button>
            <Button
              useMui
              onClick={() => {
                setShowStartTabModal(true)
                setShowSelectTabPaymentMethod(false)
              }}
            >
              <span>{APPLICATION_STRINGS.AUTHORIZE_LATER}</span>
            </Button>
          </div>
        }
      />
    )
  }

  if (showStartTabModal) {
    return (
      <ConfirmModalV2
        headerText={'Tab Name'}
        singleButtonOnly
        initialInputValue={getInitialInputValue()}
        buttonTwoText={'SAVE'}
        onButtonTwoClick={initiateTabAndCloseModal}
        inputRef={tabNameInputRef}
        useInput={true}
      />
    )
  }

  const handleCardTransaction = () => {
    const total = success?.totalAmountInCents ?? totalInCents
    const roundupEnabled = getRoundUpForCharityFeature(total) && !tipOnCardReader && !isBackBar

    // navigate to signature screen if order is free.
    if (!roundupEnabled && total === 0) {
      history.push('/signature')
    }

    if (signatureRequired && cardTipEnabledOnPos) {
      history.push('/signature')
      return
    }

    if (roundupEnabled) {
      history.push({
        pathname: '/charity',
        state: {
          fromCardLoader: true,
          totalInCents: total,
        },
      })
      return
    }

    history.push('/card-reader')
  }

  const handleScanScreenSelection = (scanScreenPath) => {
    const total = success?.totalAmountInCents ?? totalInCents
    const roundupEnabled = getRoundUpForCharityFeature(total) && !tipOnCardReader && !isBackBar

    if (tipEnabled) {
      history.push({
        pathname: '/signature',
        state: {
          addTipThenRedirectPath: scanScreenPath,
        },
      })
      return
    }

    if (roundupEnabled) {
      history.push({
        pathname: '/charity',
        state: {
          fromScanScreen: true,
          totalInCents: total,
          redirectPath: scanScreenPath,
        },
      })
      return
    }

    history.push(scanScreenPath)
  }

  /**
   * addToTab logic:
   *
   *          order in Stadium   | order not in Stadium
   *         +-------------------+---------------------+
   * online  | call mutation api | add items locally   |
   *         +-------------------+---------------------+
   * offline | can't add items   | add items locally   |
   *         +-------------------+---------------------+
   *
   */
  const addToTab = () => {
    const networkConnected = getNetworkAvailableValue()

    startSpinner(APPLICATION_STRINGS.PROCESSING)

    //Online processing
    if (networkConnected) {
      //Order aready in Stadium then we can mutate the order
      if (orderInProgress.wasCreatedInStadium) {
        // should adding items fail, redirect to orders
        const failureCallback = () => {
          dispatch(setRoute('/orders'))
        }

        addItemsToTab(failureCallback)
        return
      }
      //Order is not in Stadium so even we're online we can't mutate we can just do a local update
      addItemsToTabOffline()
      return
    }

    //offline and order already in stadium then we need to error off
    if (orderInProgress.wasCreatedInStadium) {
      ToastManager.error("Items can't be added offline for this order")
      stopSpinner()
      return
    }

    if (cloudPrintingEnabled) {
      dispatch(setKitchenPrintErrorDialogOpen(true))
    }

    //offline and order is not in stadium
    addItemsToTabOffline()
  }

  const offlineModalScanScreenSubtext = isMobileViewPort
    ? 'This payment tender is not\n supported while the device is offline'
    : 'This payment tender is not supported\nwhile the device is offline'

  const offlineGiftCardModalSubtext = isMobileViewPort
    ? 'Gift card tender is not supported\n while the device is offine'
    : 'Gift card tender is not supported while\n the device is offine'

  const deviceIsOfflineSubtext = 'Sorry, this device is now offline. Please choose another method of payment.'

  const trueButtonCount = !!cashEnabled + !isEmpty(noOpPayments) + !!mobilePaySetting + !!ticketEnabled + !!giftCardEnabled + 1

  let buttonCount
  if (trueButtonCount > 4) {
    buttonCount = 4
  } else {
    buttonCount = trueButtonCount
  }

  const oddButtonCount = buttonCount % 2 === 1

  const showOtherButton = trueButtonCount > 3

  const buttons = []
  if (!tabsModeEnabled) {
    buttons.push(
      <Button
        useMui
        className={cn([styles.checkout, styles.short])}
        disabled={buttonsDisabled}
        onClick={() => loadRemoteOrderTotal()}
      >
        {'Card'}
      </Button>,
    )
  } else {
    if (!isTabOrder) {
      buttons.push(<PayNowButton className={cn([styles.checkout, styles.short])} />)
    }
  }
  // Quick Service Only Buttons
  if (!tabsModeEnabled) {
    if (cashEnabled) {
      buttons.push(
        <Button
          useMui
          className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length + 1 ? styles.long : styles.short])}
          onClick={() => history.push('/change-calculator')}
        >
          {'Cash'}
        </Button>,
      )
    }

    if ((buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) && mobilePaySetting) {
      buttons.push(
        <Button
          useMui
          className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length + 1 ? styles.long : styles.short])}
          onClick={() => {
            // Rich Checkout "POS Shows QR"
            if (mobilePaySetting === 'pos_shows_qr') {
              if (cartHasPriceOverride) {
                dispatch(setBlockRichCheckoutOrderDialog(true, APPLICATION_STRINGS.PRICE_OVERRIDE_RICH_CHECKOUT_CLOSING_ORDER))
                return
              }
              if (cartHasOpenItem) {
                dispatch(setBlockRichCheckoutOrderDialog(true, APPLICATION_STRINGS.OPEN_PRICE_RICH_CHECKOUT_CLOSING_TABS_DIALOG))
                return
              }
              if (isOrderTaxExempt) {
                dispatch(setBlockRichCheckoutOrderDialog(true, APPLICATION_STRINGS.TAX_EXEMPT_RICH_CHECKOUT_CLOSING_TABS_DIALOG))
                return
              }
              history.push('/rich-checkout')
              return
            }

            if (ppiConfigurationError) {
              dispatch(SetOfflineDialog(true, APPLICATION_STRINGS.PPI_NETWORK_ERROR_MESSAGE))
              return
            }

            // QR Pay "POS Scans QR"
            checkIfOfflineWithCallback(
              () => handleScanScreenSelection('/qr-pay'),
              offlineModalScanScreenSubtext,
              disablePPISplitOffline,
            )
          }}
        >
          {mobilePaySetting === 'pos_shows_qr' ? 'App' : ppiQRPayTender?.name ? ppiQRPayTender?.name : 'QR Pay'}
        </Button>,
      )
    }

    if ((buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) && ticketEnabled) {
      buttons.push(
        <Button
          useMui
          className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length + 1 ? styles.long : styles.short])}
          onClick={() => {
            if (ppiConfigurationError) {
              dispatch(SetOfflineDialog(true, APPLICATION_STRINGS.PPI_NETWORK_ERROR_MESSAGE))
              return
            }

            checkIfOfflineWithCallback(
              () => handleScanScreenSelection('/ticket'),
              offlineModalScanScreenSubtext,
              disablePPISplitOffline,
            )
          }}
        >
          {ppiTicketTender?.name ? ppiTicketTender?.name : 'Ticket'}
        </Button>,
      )
    }

    if ((buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) && giftCardEnabled) {
      buttons.push(
        <Button
          useMui
          className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length + 1 ? styles.long : styles.short])}
          onClick={() => {
            if (ppiConfigurationError) {
              dispatch(SetOfflineDialog(true, APPLICATION_STRINGS.PPI_NETWORK_ERROR_MESSAGE))
              return
            }

            checkIfOfflineWithCallback(
              () => handleScanScreenSelection('/gift-card'),
              offlineGiftCardModalSubtext,
              disablePPISplitOffline,
            )
          }}
        >
          {ppiGiftcardTender?.name ? ppiGiftcardTender?.name : 'Gift Card'}
        </Button>,
      )
    }

    if (showOtherButton || (buttons.length < 4 && !isEmpty(noOpPayments))) {
      buttons.push(
        <Button
          useMui
          className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length + 1 ? styles.long : styles.short])}
          onClick={() => history.push('/tender-selection')}
        >
          Other
        </Button>,
      )
    }

    buttons.push(
      <Button
        useMui
        className={cn([styles.checkout, trueButtonCount >= 2 ? styles.long : styles.short])}
        onClick={() =>
          checkIfOfflineWithCallback(() => setShowSplitPayDialog(true), deviceIsOfflineSubtext, !isUniqueInvoiceNumbersEnabled)
        }
      >
        Split
      </Button>,
    )
  }

  const handleStartTab = () => {
    if (requirePaymentForTabs) {
      dispatch(setBridgePaymentMethod(bridgePaymentMethods.executeAuthRequest))
      history.push('/card-reader', { redirectPath: '/concession-order', authTabs: true })

      return
    }
    setShowSelectTabPaymentMethod(true)
  }

  if (tabsModeEnabled) {
    if (isTabOrder) {
      buttons.push(
        <Button
          useMui
          className={cn([styles.checkout, styles.long])}
          disabled={!hasCartItems || mutationInProgress}
          onClick={addToTab}
        >
          {APPLICATION_STRINGS.ADD_TO_TAB}
        </Button>,
      )
    } else {
      buttons.push(
        <Button
          useMui
          className={cn([styles.checkout, styles.short])}
          disabled={!hasCartItems || mutationInProgress}
          onClick={handleStartTab}
        >
          Start a Tab
        </Button>,
      )
    }
  }

  if (showSplitPayDialog) {
    modal = <ConfirmSplitPaymentDialog onClose={() => setShowSplitPayDialog(false)} fromConcessions={true} />
  }

  const headerText = isTabOrder ? orderInProgress.tabName : '#' + confirmationNumber
  const hasFewItems = cartItems?.length < 5

  if (!hasCartItems) {
    return null
  }

  return (
    <div className={classNames}>
      <div className={styles.cartButtonContainer}>
        <Button useMui className={styles.showCartButton} onClick={() => setShowCart(true)}>
          <span className={styles.quantity}>{itemCount}</span>
          <span className={styles.title}>View Cart</span>
          <span className={styles.total}>{total}</span>
        </Button>
      </div>
      <SidebarModal
        show={showCart}
        title={headerText}
        className={styles.cart}
        onClose={() => setShowCart(false)}
        isShowClear={true}
        clearCart={clearItemsAndPromotions}
      >
        <div className={styles.bodyContainer}>
          <div className={cn(styles.scrollable, styles[`buttons-${buttons.length}`], { [styles.fewItems]: hasFewItems })}>
            <CartItems
              className={styles.cartItems}
              discountsAvailable={discountsAvailable}
              networkAvailable={networkAvailable}
              total={total}
              clearItemsAndPromotions={clearItemsAndPromotions}
              remoteOrderTotalLoading={loading}
            />
          </div>
          <div className={cn(styles.buttonContainer, { [styles.fewItems]: hasFewItems })}>{buttons}</div>
        </div>
      </SidebarModal>
      {modal}
      {offlineModal}
    </div>
  )
}

export default withRouter(CartModal)
